import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, MarketSelector } from "@obg/ngx.core";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { RegistrationAction } from "../registration.action";

import { REGISTRATION_WITH_SPID_ID_ACTION_TYPE, RegistrationWithSPIDAction } from "./registration-with-spid.action";
import {
	GetSPIDJwtTokenPayload,
	GetSPIDAuthorizationUrlPayload,
	SPIDRegistrationPayload,
} from "./registration-with-spid.model";
import { RegistrationWithSPIDService } from "./registration-with-spid.service";

@Injectable()
export class RegistrationWithSPIDEffect {
	getSPIDAuthorizationUrl$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getSPIDAuthorizationUrl),
			switchMap((action: ActionWithPayload<GetSPIDAuthorizationUrlPayload>) =>
				this.registrationWithSPIDService.getSPIDAuthorizationUrl(action.payload).pipe(
					map((response) => {
						return this.action.getSPIDAuthorizationUrlSuccess(response);
					}),
					catchError((response: AppError) => of(this.action.getSPIDAuthorizationUrlFail(response)))
				)
			)
		)
	);

	getSPIDJwtToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getSPIDJwtToken),
			switchMap((action: ActionWithPayload<GetSPIDJwtTokenPayload>) =>
				this.registrationWithSPIDService
					.getSPIDJwtToken({
						sessionId: action.payload.sessionId,
						sessionKey: action.payload.sessionKey,
						transactionId: action.payload.transactionId,
					})
					.pipe(
						map((response) => this.action.getSPIDJwtTokenSuccess(response)),
						catchError((response: AppError) => of(this.action.getSPIDJwtTokenFail(response)))
					)
			)
		)
	);

	getSPIDRegistrationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationToken),
			map((action: ActionWithPayload<SPIDRegistrationPayload>) => action.payload),
			withLatestFrom(this.store.select(this.marketSelector.getCurrent())),
			switchMap(([payload, market]) =>
				this.registrationWithSPIDService.getRegistrationToken(payload, market).pipe(
					switchMap((response) =>
						of(
							this.action.getRegistrationTokenSuccess({
								...response,
								credential: {
									email: payload.email,
									password: payload.password,
									username: payload.username,
								},
							}),
							this.action.setRegistrationDetails(payload)
						)
					),
					catchError((response: AppError) => of(this.action.getRegistrationTokenFail(response)))
				)
			)
		)
	);

	setRegistrationDetails$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_SPID_ID_ACTION_TYPE.setRegistrationDetails),
			map((x: ActionWithPayload<SPIDRegistrationPayload>) =>
				this.registrationWithSPIDService.mapToRegistrationPayload(x.payload)
			),
			switchMap((payload) => [this.registrationAction.setRegistrationDetails(payload)])
		)
	);

	constructor(
		private actions$: Actions,
		private action: RegistrationWithSPIDAction,
		private registrationAction: RegistrationAction,
		private registrationWithSPIDService: RegistrationWithSPIDService,
		private marketSelector: MarketSelector,
		private store: Store
	) {}
}
